/**
 * Trucking Calendar advanced notification settings — collapsible
 * "Notification types" section (reminders, new events, status changes).
 */

import React from 'react';
import type { PageNotificationSettings } from '@/types/notification.types';
import type { StatusTransition } from '../utils/settingsLogic';

/**
 * Reminder timing options for trucking calendar
 */
const REMINDER_TIMING_OPTIONS = [
  { value: 0, label: 'At time of event' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
];

interface TruckingCalendarAdvancedSettingsProps {
  pageSettings: PageNotificationSettings | undefined;
  isDisabled: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onSetReminderMinutes: (minutes: number) => void;
  onToggleReminderEnabled: () => void;
  onToggleNewEventEnabled: () => void;
  onToggleStatusChangeEnabled: () => void;
  onToggleStatusTransition: (transition: StatusTransition) => void;
}

export const TruckingCalendarAdvancedSettings: React.FC<TruckingCalendarAdvancedSettingsProps> = ({
  pageSettings,
  isDisabled,
  isExpanded,
  onToggleExpanded,
  onSetReminderMinutes,
  onToggleReminderEnabled,
  onToggleNewEventEnabled,
  onToggleStatusChangeEnabled,
  onToggleStatusTransition,
}) => {
  return (
    <>
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-ink-body hover:bg-surface-muted transition-colors border-t border-line-subtle"
      >
        <span className="flex items-center gap-1.5">
          <i className="fa-solid fa-sliders w-3"></i>
          Notification types
        </span>
        <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Event Reminders */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-clock text-warning-mid w-4 text-sm"></i>
              <span className="text-sm text-ink-body">Event reminders</span>
            </div>
            <div className="flex items-center gap-2">
              {pageSettings?.reminderEnabled !== false && (
                <select
                  value={pageSettings?.reminderMinutes ?? 30}
                  onChange={(e) => onSetReminderMinutes(parseInt(e.target.value, 10))}
                  disabled={isDisabled}
                  className="text-xs border border-line rounded px-1.5 py-0.5 bg-surface"
                >
                  {REMINDER_TIMING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={onToggleReminderEnabled}
                disabled={isDisabled}
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  pageSettings?.reminderEnabled !== false ? 'bg-warning-mid' : 'bg-surface-deep'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-surface rounded-full shadow-sm transition-transform ${
                  pageSettings?.reminderEnabled !== false ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>

          {/* New Events */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-calendar-plus text-hue-teal-deep w-4 text-sm"></i>
              <span className="text-sm text-ink-body">New scheduled events</span>
            </div>
            <button
              onClick={onToggleNewEventEnabled}
              disabled={isDisabled}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                pageSettings?.newEventEnabled !== false ? 'bg-teal-500' : 'bg-surface-deep'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-surface rounded-full shadow-sm transition-transform ${
                pageSettings?.newEventEnabled !== false ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Status Changes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-truck-moving text-hue-sky-deep w-4 text-sm"></i>
                <span className="text-sm text-ink-body">Status changes</span>
              </div>
              <button
                onClick={onToggleStatusChangeEnabled}
                disabled={isDisabled}
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  pageSettings?.statusChangeEnabled !== false ? 'bg-sky-500' : 'bg-surface-deep'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-surface rounded-full shadow-sm transition-transform ${
                  pageSettings?.statusChangeEnabled !== false ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
            </div>
            {pageSettings?.statusChangeEnabled !== false && (
              <div className="flex flex-wrap gap-1.5 ml-6">
                {(['toScheduled', 'toPickedUp', 'toDelivered'] as const).map((transition) => {
                  const labels = { toScheduled: 'Scheduled', toPickedUp: 'Picked Up', toDelivered: 'Delivered' };
                  const transitionSettings = pageSettings?.statusTransitions || { toScheduled: true, toPickedUp: true, toDelivered: true };
                  const isChecked = transitionSettings[transition] !== false;
                  return (
                    <label
                      key={transition}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all ${
                        isChecked ? 'bg-hue-sky-soft text-hue-sky-deep ring-1 ring-hue-sky-border-strong' : 'bg-surface-inset text-ink-muted hover:bg-surface-strong'
                      }`}
                    >
                      <input type="checkbox" className="sr-only" checked={isChecked} onChange={() => onToggleStatusTransition(transition)} disabled={isDisabled} />
                      {isChecked && <i className="fa-solid fa-check text-[8px]"></i>}
                      {labels[transition]}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
